<template>
  <div>
    <h3 class="text-base leading-24 font-semibold mb-2">
      Период
    </h3>
    <div class="flex items-start">
      <ul class="mr-12" @mouseleave="hoverButton(true)">
        <li
          v-for="(item, index) in dateOptions"
          :key="index"
          class="cursor-pointer px-6 py-2 text-sm leading-24 calendar-modal__button"
          :class="{ 'calendar-modal__button--active': index === option && !isHover }"
          @click.stop="choseOption(item.date, index)"
          @mouseover="hoverButton(false, item.date)"
        >
          {{ item.label }}
        </li>
      </ul>
      <div class="calendar-modal__wrapper" @click="isCustomPick = true">
        <flat-pickr
          v-model="date"
          class="calendar-modal__input"
          :config="configDateTimePicker"
          placeholder="Inline Picker"
          @on-change="choseCustom"
          @input="selectDate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { getCommonDateOptions, getReportDateOptions } from '@/components/calendar/dateOptions';

import flatPickr from 'vue-flatpickr-component';
import { Russian } from 'flatpickr/dist/l10n/ru';

import 'dayjs/locale/ru';
import 'flatpickr/dist/flatpickr.css';

dayjs.locale('ru');
dayjs.extend(customParseFormat);
dayjs.extend(utc);

export default {
  name:       'Calendar',
  components: {
    flatPickr,
  },
  props: {
    option: {
      type:    Number,
      default: 0,
    },
    maxRangeMonth: {
      type:    Number,
      default: 0,
    },
    customDate: {
      type:    Array,
      default: null,
    },
    isFromReport: {
      type:    Boolean,
      default: false,
    },
  },
  data() {
    return {
      tempDate:             [],
      date:                 [],
      selectedDate:         '',
      isCustomPick:         false,
      isHover:              false,
      configDateTimePicker: {
        inline:     true,
        mode:       'range',
        maxDate:    'today',
        dateFormat: 'd-m-Y',
        locale:     Russian,
      },
      dateOptions: this.isFromReport ? getReportDateOptions() : getCommonDateOptions(),
    };
  },
  created() {
    this.setInitialDate();
  },
  methods: {
    /**
     * Получить запарсенную время
     *
     * @param {Date[] | String[]} date - исходное время
     * @param {Boolean} [isCustom] - выбран ли дата в ручную
     *
     * @return {String[]}
     */
    getParsedTime(date, isCustom) {
      if (isCustom) {
        return [
          dayjs(date[0]).startOf('day').startOf('day').format('YYYY-MM-DD'),
          dayjs(date[1]).endOf('day').endOf('day').format('YYYY-MM-DD'),
        ];
      }


      return [
        dayjs(date[0], 'DD-MM-YYYY').startOf('day').format('YYYY-MM-DD'),
        dayjs(date[1], 'DD-MM-YYYY').endOf('day').format('YYYY-MM-DD'),
      ];
    },

    /**
     * Установить дефолтный период времени
     */
    setInitialDate() {
      if (this.customDate) {
        // eslint-disable-next-line no-multi-assign
        this.date = this.tempDate = this.customDate;
        return;
      }

      if (this.option < 0 || this.option > this.dateOptions.length - 1) return;

      const { date } = this.dateOptions[this.option];

      // eslint-disable-next-line no-multi-assign
      this.date = this.tempDate = date;

      this.$emit('init', this.date);
    },

    /**
     * При наведение на оидн из опций, показывает календаре какие даты будет выбран
     *
     * @param {Boolean} isLeave - mouseOver ли событие
     * @param {Date | String} [date] - выбранная дата
     */
    hoverButton(isLeave, date) {
      this.isCustomPick = false;
      this.resetDateRange(false);

      if (isLeave) {
        this.date = JSON.parse(JSON.stringify(this.tempDate));
        this.tempDate = [];
        this.isHover = false;
        this.limitDateRange();
        return;
      }

      if (!this.tempDate.length) this.tempDate = JSON.parse(JSON.stringify(this.date));

      this.isHover = true;
      this.date = date;
    },

    /**
     * Задает максимальный срок выбора; максимальный срок берется из maxRangeMonth.
     *
     * @param {String} [date] - исходное время
     */
    limitDateRange(date) {
      if (date) this.selectedDate = date;

      if (!this.maxRangeMonth || !this.selectedDate) return;

      const parsedDate = dayjs(this.selectedDate, 'DD-MM-YYYY');

      this.$set(this.configDateTimePicker, 'minDate', parsedDate
        .subtract(this.maxRangeMonth, 'month').toString());

      if (dayjs().diff(parsedDate, 'month') >= this.maxRangeMonth) {
        this.$set(this.configDateTimePicker, 'maxDate', parsedDate.add(this.maxRangeMonth, 'month').toString());
      } else {
        this.$set(this.configDateTimePicker, 'maxDate', 'today');
      }
    },

    /**
     * Выбрать дату из календаря, задает что выбор пользовательский
     *
     * @param {String} date - выбранная дата
     */
    selectDate(date) {
      if (this.isHover) return;

      this.isCustomPick = true;

      if (date.split(' — ').length >= 2) return;

      this.limitDateRange(date);
    },

    /**
     * Выбран ли пользовательский диапозон дат в календаре
     *
     * @param {Date | String} date - выбранный дата
     */
    choseCustom(date) {
      if (!this.isCustomPick || date.length < 2) return;

      const formattedDate = `${dayjs(date[0]).format('DD.MM.YY')} - ${dayjs(date[1]).format('DD.MM.YY')}`;

      this.$emit('change', this.getParsedTime(date, true), -1, formattedDate);
      this.$emit('toggle', false);
      this.resetDateRange();
    },

    /**
     * Выбрать опцию и по нему выбрать даты
     *
     * @param {Date | String} date - выбранный дата
     * @param {Number} index - Индекс опций
     */
    choseOption(date, index) {
      this.isCustomPick = false;
      this.date = date;
      this.tempDate = date;
      const { label } = this.dateOptions[index];

      this.$emit('change', this.getParsedTime(date), index, label);
      this.$emit('toggle', false);
      this.resetDateRange();
    },

    /**
     * Сбрасывает ограничение по сроку выбору дат
     *
     * @param {Boolean} [isFullReset] - сбросить все данные
     */
    resetDateRange(isFullReset = true) {
      if (!this.maxRangeMonth) return;

      this.$set(this.configDateTimePicker, 'minDate', null);
      this.$set(this.configDateTimePicker, 'maxDate', 'today');
      if (isFullReset) this.selectedDate = '';
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/vuexy/_variables.scss';
  @import '@/assets/scss/krisha/calendar-modal.scss';
</style>
